// The Danger zone (APS-412, admin-d.md "Danger"; the console's Settings › General, APS-401): the
// page's last section, "Zona de perigo", unboxed under its rule: the action's title in the
// crash tone, one paragraph of what happens, and the danger `.btn` that opens the confirm
// (ConfirmDialog: the Motivo and, for an erasure, the subject typed again).
import { type ReactNode, useId, useState } from 'react'
import { useI18n } from '../../i18n'
import { ConfirmDialog } from './ConfirmDialog'

export interface DangerAction {
  /** The row's title, in the crash tone ("Excluir esta organização"). */
  title: string
  /** One paragraph: what happens, and when. */
  body: ReactNode
  /** The opening button's words. */
  action: string
  confirmTitle: string
  confirmBody: ReactNode
  confirmLabel: string
  /** The confirm's dismiss button, when "Cancelar" would mean the opposite of the confirm. */
  dismissLabel?: string | undefined
  /** Confirmed only once this is typed again exactly (an erasure's org name or user hash). */
  typed?: string | undefined
  /** Refuse a Motivo that looks like an email (erasures: the API refuses it too). */
  noEmails?: boolean | undefined
  busy?: boolean | undefined
  /** The API's answer to the last try, under the row. */
  error?: ReactNode
  onConfirm: (reason: string) => void
}

/** One row of the zone: what it does, its danger button, and the confirm the button opens. */
export function DangerRow({
  title,
  body,
  action,
  confirmTitle,
  confirmBody,
  confirmLabel,
  dismissLabel,
  typed,
  noEmails,
  busy,
  error,
  onConfirm,
}: DangerAction) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <div className="dz-row">
        <div>
          <b>{title}</b>
          <p>{body}</p>
        </div>
        <button type="button" className="btn danger" disabled={busy} onClick={() => setOpen(true)}>
          {action}
        </button>
      </div>
      {error}
      <ConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        title={confirmTitle}
        body={confirmBody}
        confirmLabel={confirmLabel}
        dismissLabel={dismissLabel}
        typed={typed}
        noEmails={noEmails}
        busy={busy}
        onConfirm={onConfirm}
      />
    </>
  )
}

/**
 * The zone: its rule and title, then `before` (other rows, such as a subscription's
 * cancellation, APS-468) and its own row, the most destructive last.
 */
export function DangerZone({ before, ...row }: DangerAction & { before?: ReactNode }) {
  const { t } = useI18n()
  const id = useId()
  return (
    <section className="sec" aria-labelledby={id}>
      <div className="sec-h">
        <h2 id={id}>{t('kit.danger.section')}</h2>
      </div>
      {before}
      <DangerRow {...row} />
    </section>
  )
}
